import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { StandingsTable, type StandingsTableRow } from './StandingsTable';
import {
  roundStandingsService,
  type RoundStandingsPage,
  type StandingCategory,
} from '../../services/round-standings';
import type { RoundType } from '../../types/competition-round';

interface RoundStandingsProps {
  competitionSlug: string;
  roundId: string;
  /** The round's discipline: only a lead round can mix top rope and lead. */
  roundType: RoundType;
}

interface ClassStandings {
  category: StandingCategory;
  /** The three things a results table says. */
  rows: StandingsTableRow[];
  /**
   * Whether this class climbed partly on top rope and partly on lead — read off
   * the cards themselves rather than asked of the server, because it is a fact
   * about the afternoon and not a setting.
   *
   * It is why the table says so: a led route beats a top roped one however far
   * up each climber got, so a sheet with both on it puts a one-hold lead above a
   * top roped top, and without a word of explanation that reads as a mistake.
   */
  mixedStyles: boolean;
}

/**
 * The standings of a closed round, one table per class — the sheet the room
 * reads when the afternoon is over.
 *
 * **Three columns and nothing else**: who, for which squad, and where they came.
 * The cards behind a position are the desk's business and are read from the
 * panel's board; here they are only what the order is worked out from.
 *
 * **Every class at once, no tabs.** A tab is right while a round is being
 * climbed, when somebody is working through one class; at the end everybody
 * wants their own table, and a tab would hide three of the four.
 *
 * The places are the server's, by the competition's regulation — the same call
 * the panel's board makes — so a position never reads two ways depending on
 * which page shows it. Somebody with no card anywhere in the round is not placed
 * at all — the ranking's own rule — so they are off the sheet rather than
 * sharing last place with everybody else who did not climb.
 */
export function RoundStandings({ competitionSlug, roundId, roundType }: RoundStandingsProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState<RoundStandingsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setFailed(false);
    roundStandingsService
      .get(competitionSlug, roundId)
      .then((standings) => {
        if (!cancelled) setPage(standings);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [competitionSlug, roundId]);

  const classes = useMemo<ClassStandings[]>(() => {
    if (!page) return [];

    return page.categories.map((category) => {
      const entered = page.entries.filter((entry) => entry.category_id === category.id);

      const rows = entered
        .filter((entry) => entry.rank != null)
        .map((entry) => ({
          id: entry.id,
          name: entry.full_name,
          team: entry.team_name,
          position: entry.rank as number,
          tiebreak: entry.tiebreak,
        }))
        // Tied athletes share a position, so the name decides the order they are
        // printed in — otherwise two people on the same place would swap rows
        // between reloads.
        .sort(
          (a, b) =>
            a.position - b.position ||
            (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
        );

      const styles = new Set(
        entered.flatMap((entry) =>
          entry.cards.map((card) => card.climbed_as).filter((style) => style != null)
        )
      );

      return { category, rows, mixedStyles: roundType === 'Lead' && styles.size > 1 };
    });
  }, [page, roundType]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="round-standings-loading">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (failed) {
    return <p className="py-6 text-center text-muted-foreground">{t('errors.loadFailed')}</p>;
  }

  // A round closed with nobody judged on anything: say so once, rather than
  // printing an empty table per class.
  if (classes.every((group) => group.rows.length === 0)) {
    return (
      <div className="py-8 text-center text-muted-foreground" data-testid="round-standings-empty">
        <Trophy className="mx-auto mb-4 size-12 opacity-50" />
        <p>{t('competitionRounds.standings.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="round-standings">
      {classes.map(({ category, rows, mixedStyles }) => (
        <section key={category.id} className="space-y-2" data-testid={`standings-${category.id}`}>
          <h3 className="font-semibold">{category.name}</h3>

          {/* Only where the class actually climbed both ways: on a class that
              all led, or all top roped, this would be noise about a rule that
              changed nothing. */}
          {mixedStyles && rows.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="standings-mixed-styles">
              {t('leadRounds.style.mixedNote')}
            </p>
          )}

          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('competitionRounds.standings.empty')}</p>
          ) : (
            <StandingsTable rows={rows} />
          )}
        </section>
      ))}
    </div>
  );
}

export default RoundStandings;
